import React, {useEffect, useState} from 'react';
import OverallStandardSpeedometer from "./overallStandardSpeedometer";
import StandardDataModal from "./standardDataModal";
import {useNavigate} from "react-router-dom";
import Loader from "../../components/loader";
import ToSModal from "../../components/tosModal";
import getWithAuth from "../../context/getWithAuth";
import DOMPurify from "dompurify";
import StudentOverview from "./studentOverview";

function Data({user, rosters, setRosters, standardSets, setStandardSets, studentContext, setStudentContext, knowShowCharts, setKnowShowCharts, fetchStudentData}) {
    const [standardDataModal, setStandardDataModal] = useState(null);
    const [selectedStandardSet, setSelectedStandardSet] = useState(null);
    const [studentData, setStudentData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [standardSetData, setStandardSetData] = useState(null);
    const [assessmentItems, setAssessmentItems] = useState(null);
    const [standardElements, setStandardElements] = useState(null);
    const [recentsCount, setRecentsCount] = useState(5);

    const renderHtml = (htmlString) => {
        const sanitizedHtml = DOMPurify.sanitize(htmlString);
        return { __html: sanitizedHtml };
    };

    const uniqueBy = (arr, keyFn) => {
        const seen = new Set();
        return arr.filter(item => {
            const key = keyFn(item);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const getStudentDataByPrep = async (standardSet) => {
        if (user.userAttributes) {
            try {
                getWithAuth('/data/getStudentDataByPrep', user.userAttributes['custom:school'], {
                    userId: user.userContext._id.$oid,
                    standardSet: standardSet.prepTitle,
                    role: user.userContext.role
                })
                    .then(async r => {
                        setStandardSetData(r.standardSet);
                        setStandardElements(r.standardElements);
                        setAssessmentItems(r.assessmentItems);
                        setStudentData(r.studentData);
                    })
                    .then(() => setIsLoading(false))
            } catch (error) {
                console.error('Error fetching standards data:', error)
            }
        }
    }

    const navigate = useNavigate();

    // const handleStandardSetSelect = async (prep) => {
    //     setIsLoading(true);
    //     await getStudentDataByPrep(prep);
    //     await setSelectedStandardSet(prep);
    // }

    const standardActiveCheck = (standard) => {
        if (studentData && studentData.length > 0) {
            let today = new Date()
            const activeRosters = rosters.filter(roster => new Date(roster.startDate.$date) <= today && new Date(roster.endDate.$date) >= today)
            for (let i = 0; i < activeRosters.length; i++) {
                if (studentData.flatMap(item => item.active[standardSetData.prepTitle]).filter(item => item.standard === standard.standardCode).filter(entry => (Object.keys(entry).includes('dateAssigned') && (new Date(entry.dateAssigned.$date) >= new Date(activeRosters[i].startDate.$date) && new Date(entry.dateAssigned.$date) <= new Date(activeRosters[i].endDate.$date))) || entry.type === "Recall").length > 0) {
                    return true
                }
                if (activeRosters[i].assessmentHistory.flatMap(item => item.knowShowId).some(element => new Set(standardElements.filter(chart => chart.standard === standard.standardCode)).has(element))) {
                    return true
                }
            }
        }

    }

    useEffect(() => {
        if (user.userContext && selectedStandardSet && selectedStandardSet !== 'studentLookup') {
            setIsLoading(true)
            let intervalId;

            const startInterval = () => {
                if (!intervalId) {
                    intervalId = setInterval(() => getStudentDataByPrep(selectedStandardSet), 15000);
                }
            };

            const stopInterval = () => {
                if (intervalId) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
            };

            const handleVisibilityChange = () => {
                if (document.visibilityState === 'visible') {
                    startInterval();
                } else {
                    stopInterval();
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);

            // Start the interval initially if the page is visible
            if (document.visibilityState === 'visible') {
                getStudentDataByPrep(selectedStandardSet); // Fetch data immediately
                startInterval();
            }

            return () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                stopInterval();
            };
        }
    }, [user.userContext, selectedStandardSet]);

    useEffect(() => {
        if (standardDataModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [standardDataModal])

    const today = new Date()

    useEffect(() => {
        if (studentData && studentData.length > 0 && !Object.keys(studentData[0].active).includes(selectedStandardSet.prepTitle)) {
            setIsLoading(true)
            getStudentDataByPrep(selectedStandardSet)
        }
    }, [studentData])

    if (!user.userContext) {
        return <Loader message={'Fetching user data'}/>
    } else if (!user.userContext.termsAccepted) {
        return <ToSModal user={user}/>
    } else if (user.userContext.role === 'student') {
        navigate('/home')
    } else if ( !rosters || !standardSets) {
        const loadingItems = [
            !standardSets && 'Standard Sets',
            !rosters && "Rosters",
            // !knowShowCharts && "Standard Elements",
            // !assessmentItems && "Assessment Items",
        ].filter(Boolean); // Filter out false/null values

        const fetchingText = loadingItems.length > 1
            ? `Fetching: ${loadingItems.slice(0, -1).join(", ")} and ${loadingItems.slice(-1)}`
            : `Fetching: ${loadingItems[0] || ""}`;
        return <Loader message={fetchingText}/>
    } else {
            return (
                <div>
                    <div className={'flex rounded-t gap-4 overflow-x-auto mx-4 relative'}>
                        {(standardSets) && standardSets.filter(prep => rosters.filter(roster => new Date(roster.endDate.$date) > today && new Date(roster.startDate.$date) < today).map(roster => roster.prep).includes(prep.prepTitle)).sort((a, b) => a.prepTitle.toLowerCase().localeCompare(b.prepTitle.toLowerCase()))
                            .map(standardSet =>
                                (selectedStandardSet === standardSet) ?
                                    <button
                                        className={'font-bold min-w-fit p-4 text-lg text-dutch_white-100 rounded-t bg-columbia_blue-700 select-none cursor-default'}>
                                        {standardSet.prepTitle}
                                    </button>
                                    :
                                    <button
                                        className={'font-bold min-w-fit p-4 text-lg rounded-t bg-columbia_blue-300 unselected-tab select-none hover:bg-columbia_blue-400 cursor-pointer'}
                                        onClick={() => setSelectedStandardSet(standardSet)}>
                                        {standardSet.prepTitle}
                                    </button>
                            )}
                        <div className={'grow'} />
                        {
                            (selectedStandardSet === 'studentLookup') ?
                                <button
                                    className={'font-bold min-w-fit p-4 text-lg text-dutch_white-100 rounded-t bg-columbia_blue-800 select-none cursor-default'}>
                                    Student Lookup
                                </button>
                                :
                                <button
                                    className={'font-bold min-w-fit p-4 text-lg rounded-t bg-columbia_blue-300 unselected-tab select-none hover:bg-columbia_blue-400 cursor-pointer'}
                                    onClick={() => setSelectedStandardSet('studentLookup')}>
                                    Student Lookup
                                </button>
                        }
                    </div>
                    {(selectedStandardSet) && (selectedStandardSet === 'studentLookup') ?
                        <div
                            className={'bg-columbia_blue-800 p-2 mx-4 shadow-lg mb-2 rounded-b min-h-screen'}>
                            <StudentOverview user={user} />
                        </div>
                            : (!isLoading) && (studentData) && (studentData.length > 0) && (Object.keys(studentData[0].active).includes(selectedStandardSet.prepTitle)) && (standardSetData) && (standardElements) ?
                            <div
                                className={'bg-columbia_blue-700 p-2 mx-4 shadow-lg mb-2 rounded-b min-h-screen'}>
                                <div
                                    className={'bg-dutch_white-100 p-4 mb-4 flex-col justify-items-start rounded relative'}>
                                    <p className={'font-bold text-columbia_blue-900'}>Recent Assignments:</p>
                                    <div className={'grid grid-cols-5 gap-2 w-full'}>
                                        {uniqueBy(rosters.filter(roster => roster.prep === selectedStandardSet.prepTitle).flatMap(item => item.assessmentHistory).sort((a,b) => new Date(b.date.$date) - new Date(a.date.$date)), item => item.knowShowId).slice(0, recentsCount).map(entry =>
                                            <div className={'w-full'}>
                                                <button
                                                    className={'rounded-t rounded-bl p-2 bg-columbia_blue-200 hover:bg-columbia_blue-300 text-columbia_blue-900 text-xs text-left h-20 w-full'} onClick={() => setStandardDataModal({
                                                    standard: standardSetData.standards.find(item => item.standardCode === standardElements.find(element => element._id.$oid === entry.knowShowId).standard),
                                                    standardCode: standardElements.find(element => element._id.$oid === entry.knowShowId).standard,
                                                    standardSet: selectedStandardSet,
                                                    standardElement: standardElements.find(element => element._id.$oid === entry.knowShowId)
                                                    })}>
                                                    <div className={'h-[95%] p-1 overflow-y-auto'} dangerouslySetInnerHTML={renderHtml((standardElements.find(item => item._id.$oid === entry.knowShowId).type === 'Recall') ? standardElements.find(item => item._id.$oid === entry.knowShowId).title : standardElements.find(item => item._id.$oid === entry.knowShowId).text)} />
                                                </button>
                                                <div
                                                    className={'relative bottom-0 bg-columbia_blue-300 w-fit justify-self-end text-xs p-1 rounded-b'}>{new Date(entry.date.$date).toLocaleDateString()}</div>

                                            </div>)}

                                    </div>
                                    {(uniqueBy(rosters.filter(roster => roster.prep === selectedStandardSet.prepTitle).flatMap(item => item.assessmentHistory).sort((a,b) => new Date(b.date.$date) - new Date(a.date.$date)), item => item.knowShowId).length > recentsCount) && <button className={'mt-2 text-xs relative bottom-0 right-0'}
                                            onClick={() => setRecentsCount(prev => prev + 5)}>Show 5 more...
                                    </button>
                                    }
                                </div>
                                <div className={'grid grid-cols-4 gap-4'}>
                                    {standardSetData.standards.map((standard) => (
                                        (rosters && studentData) ?
                                            (standardActiveCheck(standard))
                                                ?
                                                <button
                                                    className={`rounded w-full flex flex-col border-[3px] overflow-x-hidden border-columbia_blue-800 shadow-lg text-columbia_blue-800 py-2 px-4 cursor-pointer ${standard.classification !== 'base' ? 'non-base-background hover-non-base-background' : 'base-background hover-base-background'}`}
                                                    onClick={() => setStandardDataModal(
                                                        {
                                                            standard: standard,
                                                            standardCode: standard.standardCode,
                                                            standardSet: selectedStandardSet,
                                                            standardElement: null
                                                        }
                                                    )}>
                                                    <p className={'font-bold text-columbia_blue-800 select-none mx-auto'}>{standard.code}</p>
                                                    <p className={'text-columbia_blue-800 select-none mx-auto'}>{standard.title}</p>
                                                    <div className={'grow'}/>
                                                    <div className={'flex gap-6 w-fit mx-auto'}>
                                                        <OverallStandardSpeedometer
                                                            tabIndex={-1}
                                                            value={(studentData.filter(student => rosters.filter(roster => new Date(roster.startDate.$date) <= new Date() && new Date(roster.endDate.$date) >= new Date()).filter(item => item.prep === selectedStandardSet.prepTitle).flatMap(item => item.students).includes(student.studentId)).flatMap(item => item.active[selectedStandardSet.prepTitle]).filter(data => data.type === 'Recall' && data.standard === standard.standardCode).length > 0) ? studentData.filter(student => rosters.filter(roster => new Date(roster.startDate.$date) <= new Date() && new Date(roster.endDate.$date) >= new Date()).filter(item => item.prep === selectedStandardSet.prepTitle).flatMap(item => item.students).includes(student.studentId)).flatMap(item => item.active[selectedStandardSet.prepTitle]).filter(data => (data.type !== 'Recall' && data.standard === standard.standardCode && data.result !== 'pending' && (data.result === true || data.result === 0))).length + (studentData.filter(student => rosters.filter(roster => new Date(roster.startDate.$date) <= new Date() && new Date(roster.endDate.$date) >= new Date()).filter(item => item.prep === selectedStandardSet.prepTitle).flatMap(item => item.students).includes(student.studentId)).flatMap(item => item.active[selectedStandardSet.prepTitle]).filter(data => (data.type === 'Recall' && data.standard === standard.standardCode)).flatMap(item => item.recallItems).map(item => item.active).filter(item => item.correct === true).length / studentData.filter(student => rosters.filter(roster => new Date(roster.startDate.$date) <= new Date() && new Date(roster.endDate.$date) >= new Date()).filter(item => item.prep === selectedStandardSet.prepTitle).flatMap(item => item.students).includes(student.studentId)).flatMap(item => item.active[selectedStandardSet.prepTitle]).filter(data => (data.type === 'Recall' && data.standard === standard.standardCode)).flatMap(item => item.recallItems).map(item => item.active).length * studentData.filter(student => rosters.filter(roster => new Date(roster.startDate.$date) <= new Date() && new Date(roster.endDate.$date) >= new Date()).filter(item => item.prep === selectedStandardSet.prepTitle).flatMap(item => item.students).includes(student.studentId)).flatMap(item => item.active[selectedStandardSet.prepTitle]).filter(data => (data.type === 'Recall' && data.standard === standard.standardCode)).length) : studentData.filter(student => rosters.filter(roster => new Date(roster.startDate.$date) <= new Date() && new Date(roster.endDate.$date) >= new Date()).filter(item => item.prep === selectedStandardSet.prepTitle).flatMap(item => item.students).includes(student.studentId)).flatMap(item => item.active[selectedStandardSet.prepTitle]).filter(data => (data.type !== 'Recall' && data.standard === standard.standardCode && data.result !== 'pending' && (data.result === true || data.result === 0))).length}
                                                            data={[
                                                                {
                                                                    name: 'Reteach',
                                                                    value: Math.ceil(studentData.filter(student => rosters.filter(roster => new Date(roster.startDate.$date) <= new Date() && new Date(roster.endDate.$date) >= new Date()).filter(item => item.prep === selectedStandardSet.prepTitle).flatMap(item => item.students).includes(student.studentId)).flatMap(item => item.active[selectedStandardSet.prepTitle]).filter(data => (data.standard === standard.standardCode)).length * 0.6),
                                                                    color: '#c25022'
                                                                },
                                                                {
                                                                    name: 'Check-in',
                                                                    value: Math.ceil(studentData.filter(student => rosters.filter(roster => new Date(roster.startDate.$date) <= new Date() && new Date(roster.endDate.$date) >= new Date()).filter(item => item.prep === selectedStandardSet.prepTitle).flatMap(item => item.students).includes(student.studentId)).flatMap(item => item.active[selectedStandardSet.prepTitle]).filter(entry => (entry.standard === standard.standardCode)).length * 0.3),
                                                                    color: '#538ea5'
                                                                },
                                                                {
                                                                    name: 'Move On',
                                                                    value: Math.ceil(studentData.filter(student => rosters.filter(roster => new Date(roster.startDate.$date) <= new Date() && new Date(roster.endDate.$date) >= new Date()).filter(item => item.prep === selectedStandardSet.prepTitle).flatMap(item => item.students).includes(student.studentId)).flatMap(item => item.active[selectedStandardSet.prepTitle]).filter(entry => (entry.standard === standard.standardCode)).length * 0.1),
                                                                    color: '#f7f7f7'
                                                                },
                                                            ]}/>
                                                        {/*<div*/}
                                                        {/*    className={'rounded bg-dutch_white-200 shadow h-fit p-1 my-auto font-bold text-columbia_blue-800 text-xs'}>*/}
                                                        {/*    <p>Pending: {studentData.filter(student => rosters.filter(roster => roster.prep === selectedStandardSet.prepTitle).flatMap(roster => roster.students).includes(student.studentId)).flatMap(item => item.active[selectedStandardSet.prepTitle]).filter(item => item.standard === standard.standardCode && (item.result === 'pending' || (item.type === 'Recall' && item.recallItems.map(item => item.active).map(item => item.correct).includes('pending')))).length}</p>*/}
                                                        {/*</div>*/}
                                                    </div>
                                                </button>
                                                :
                                                <div
                                                    className={'rounded w-full bg-columbia_blue-400 text-columbia_blue-800 py-2 px-4 flex flex-col'}>
                                                    <p className={'font-bold text-columbia_blue-700 select-none'}>{standard.code}</p>
                                                    <p className={'text-columbia_blue-700 select-none'}>{standard.title}</p>
                                                    <div className={'grow'}/>
                                                    <p className={'text-4xl my-4 text-platinum-700 font-bold select-none'}>No
                                                        Data Collected</p>
                                                </div>
                                            : <></>))}
                                </div>
                            </div>

                            : (isLoading) ?
                                <div className={'bg-platinum-700 mx-4 h-screen rounded-b mb-2'}>
                                    <div
                                        className="flex flex-col items-center min-h-full justify-center py-20 rounded-t">
                                        {/* Loader */}
                                        <div
                                            className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-100"></div>
                                        <p className={'font-bold mt-8 text-dutch_white-300'}>Fetching Student Data</p>
                                    </div>
                                </div> :
                                <div
                                    className={'bg-columbia_blue-700 p-2 mx-4 min-h-20 border-columbia_blue-900 shadow-lg mb-2 rounded-b text-dutch_white-100 font-bold text-2xl'}>
                            Select a prep to review your data</div>}
                    {(standardDataModal) &&
                        <StandardDataModal user={user} rosters={rosters.filter(item => item.prep === standardSetData.prepTitle)} studentData={studentData}
                                           knowShowCharts={standardElements}
                                           assessmentItems={assessmentItems} standardDataModal={standardDataModal}
                                           studentIds={rosters.filter(item => item.prep === selectedStandardSet.prepTitle).flatMap(roster => roster.students)}
                                           setStandardDataModal={setStandardDataModal}
                                           resetStudentData={fetchStudentData} setStudentData={setStudentData}/>}

                </div>
            );
        }
    }


export default Data;